import { useEffect, useRef, useState } from 'react';

import FORM_KEYS from 'apps/web/constants/forms/lesson-plan/formKeys';
import useAutosizeTextArea from 'apps/web/hooks/useAutoSizeTextArea';
import { useScrollToError } from 'apps/web/hooks/useScrollToError';
import processInput from 'apps/web/utils/formInput';
import {
  shouldDisableObjective2,
  shouldDisableObjective3,
  shouldDisableObjective4,
} from 'apps/web/utils/lesson-format/objectiveFieldHelpers';

import {
  LP_PAGE_OBJECTIVE_CHAR_LIMIT,
  LP_PAGE_SUBJECT_CHAR_LIMIT,
} from '@lessonPlanner/constants';
import { Language } from '@lessonPlanner/enums';
import { LessonFormat } from '@lessonPlanner/prompts';

import { SelectField, TextAreaField, TextField } from '../form-fields';
import TranslationDropdown from '../molecules/TranslationDropdown';
import { GradeLevelDropdown } from '../Select';

export default function LocalLp({
  errors,
  watchedValues,
  triggerValidation,
  setValue,
  clearErrors,
}: {
  errors;
  watchedValues;
  triggerValidation;
  setValue;
  clearErrors;
}) {
  const [objectiveSubLabel, setObjectiveSubLabel] = useState<string>(
    'At the end of the lesson, the students will be able to...',
  );

  const lessonFormatOptions = [...Object.values(LessonFormat)];
  const isIplanSelected =
    watchedValues.lessonFormat === LessonFormat.CODE_IPLAN;

  async function updateFieldValue(fieldName, value) {
    setValue(fieldName, value);
  }

  const textAreaRefs = {
    objective: useRef<HTMLTextAreaElement>(null),
    objective2: useRef<HTMLTextAreaElement>(null),
    objective3: useRef<HTMLTextAreaElement>(null),
    objective4: useRef<HTMLTextAreaElement>(null),
  };

  useAutosizeTextArea(textAreaRefs.objective.current, watchedValues.objective);
  useAutosizeTextArea(
    textAreaRefs.objective2.current,
    watchedValues.objective2,
  );
  useAutosizeTextArea(
    textAreaRefs.objective3.current,
    watchedValues.objective3,
  );
  useAutosizeTextArea(
    textAreaRefs.objective4.current,
    watchedValues.objective4,
  );

  useScrollToError(errors);

  useEffect(() => {
    let subLabelText;
    if (watchedValues.lessonFormat === LessonFormat.CODE_IPLAN) {
      subLabelText =
        'It is recommended to have 1 objective for each learning domain [Cognitive, Psychomotor, Affective - Attitude, Affective - Values]';
    } else {
      subLabelText =
        watchedValues.language === Language.ENGLISH
          ? 'At the end of the lesson, the students will be able to...'
          : 'Ang mag-aaral ay inaasahan na...';
    }
    setObjectiveSubLabel(subLabelText);
  }, [watchedValues.language, watchedValues.lessonFormat]);

  useEffect(() => {
    if (watchedValues.gradeLevel === 'College') {
      setValue('lessonFormat', '');
      clearErrors('lessonFormat');
    }
  }, [watchedValues.gradeLevel, setValue, clearErrors]);

  useEffect(() => {
    if (watchedValues.lessonFormat !== LessonFormat.CODE_IPLAN) {
      updateFieldValue(FORM_KEYS.objective4, '');
    }
  }, [watchedValues.lessonFormat]);

  return (
    <>
      <GradeLevelDropdown
        hasCollege
        error={errors.gradeLevel}
        errorMessage={!!errors.gradeLevel && 'Please select the Grade Level'}
        label={'Grade Level'}
        updateFn={updateFieldValue}
        validateFn={triggerValidation}
        value={watchedValues.gradeLevel}
      />
      {watchedValues.gradeLevel !== 'College' && (
        <SelectField
          className="col-span-full"
          error={!!errors.lessonFormat}
          id="lessonFormat"
          label="Lesson Format"
          name="lessonFormat"
          updateFn={updateFieldValue}
          validateFn={triggerValidation}
          value={watchedValues.lessonFormat}
          errorMessage={
            !!errors.lessonFormat && 'Please select the Lesson Format'
          }
        >
          {lessonFormatOptions.map((lessonFormat, index) => (
            <option key={index} value={lessonFormat}>
              {lessonFormat}
            </option>
          ))}
        </SelectField>
      )}
      <TextField
        className="col-span-full"
        errorMessage={!!errors.subject && 'Please enter the Subject'}
        id="subject"
        label={'Subject / Discipline'}
        maxLength={LP_PAGE_SUBJECT_CHAR_LIMIT}
        name="subject"
        placeholder="Input the subject for this lesson plan"
        type="subject"
        value={watchedValues.subject}
        isMaxLengthReached={
          watchedValues.subject?.length >= LP_PAGE_SUBJECT_CHAR_LIMIT
        }
        onBlur={() => {
          processInput(
            updateFieldValue,
            triggerValidation,
            FORM_KEYS.subject,
            watchedValues.subject?.trim() || '',
          );
        }}
        onChange={(e) => {
          processInput(
            updateFieldValue,
            triggerValidation,
            FORM_KEYS.subject,
            e.target.value,
          );
        }}
      />

      <div className="col-span-full">
        <TextAreaField
          showSubLabel
          className="col-span-full"
          error={!!errors.objective}
          errorMessage={!!errors.objective && 'Please enter the Objectives'}
          id="objective"
          label="Objectives"
          maxLength={LP_PAGE_OBJECTIVE_CHAR_LIMIT}
          name="objective"
          placeholder="Input the first objective for this lesson plan"
          rows={2}
          subLabel={objectiveSubLabel}
          textAreaRef={textAreaRefs.objective}
          type="objective"
          value={watchedValues.objective}
          isMaxLengthReached={
            watchedValues.objective?.length >= LP_PAGE_OBJECTIVE_CHAR_LIMIT
          }
          onBlur={() => {
            processInput(
              updateFieldValue,
              triggerValidation,
              FORM_KEYS.objective,
              watchedValues.objective?.trim() || '',
            );
          }}
          onChange={(e) => {
            processInput(
              updateFieldValue,
              triggerValidation,
              FORM_KEYS.objective,
              e.target.value,
            );
          }}
        />

        <TextAreaField
          showSubLabel
          className="col-span-full"
          disabled={shouldDisableObjective2(watchedValues)}
          error={!!errors.objective2}
          id="objective2"
          maxLength={LP_PAGE_OBJECTIVE_CHAR_LIMIT}
          name="objective2"
          placeholder="Input the second objective for this lesson plan"
          rows={1}
          textAreaRef={textAreaRefs.objective2}
          value={watchedValues.objective2}
          isMaxLengthReached={
            watchedValues.objective2?.length >= LP_PAGE_OBJECTIVE_CHAR_LIMIT
          }
          onBlur={() => {
            processInput(
              updateFieldValue,
              triggerValidation,
              FORM_KEYS.objective2,
              watchedValues.objective2?.trim() || '',
            );
          }}
          onChange={(e) => {
            processInput(
              updateFieldValue,
              triggerValidation,
              FORM_KEYS.objective2,
              e.target.value,
            );
          }}
        />
        <TextAreaField
          showSubLabel
          className="col-span-full"
          disabled={shouldDisableObjective3(watchedValues)}
          error={!!errors.objective3}
          id="objective3"
          maxLength={LP_PAGE_OBJECTIVE_CHAR_LIMIT}
          name="objective3"
          placeholder="Input the third objective for this lesson plan"
          rows={1}
          textAreaRef={textAreaRefs.objective3}
          value={watchedValues.objective3}
          isMaxLengthReached={
            watchedValues.objective3?.length >= LP_PAGE_OBJECTIVE_CHAR_LIMIT
          }
          onBlur={() => {
            processInput(
              updateFieldValue,
              triggerValidation,
              FORM_KEYS.objective3,
              watchedValues.objective3?.trim() || '',
            );
          }}
          onChange={(e) => {
            processInput(
              updateFieldValue,
              triggerValidation,
              FORM_KEYS.objective3,
              e.target.value,
            );
          }}
        />
        {isIplanSelected && (
          <TextAreaField
            showSubLabel
            className="col-span-full"
            disabled={shouldDisableObjective4(watchedValues)}
            error={!!errors.objective4}
            id="objective4"
            maxLength={LP_PAGE_OBJECTIVE_CHAR_LIMIT}
            name="objective4"
            placeholder="Input the fourth objective for this lesson plan"
            rows={1}
            textAreaRef={textAreaRefs.objective4}
            value={watchedValues.objective4}
            isMaxLengthReached={
              watchedValues.objective4?.length >= LP_PAGE_OBJECTIVE_CHAR_LIMIT
            }
            onBlur={() => {
              processInput(
                updateFieldValue,
                triggerValidation,
                FORM_KEYS.objective4,
                watchedValues.objective4?.trim() || '',
              );
            }}
            onChange={(e) => {
              processInput(
                updateFieldValue,
                triggerValidation,
                FORM_KEYS.objective4,
                e.target.value,
              );
            }}
          />
        )}
      </div>

      <TranslationDropdown
        errors={errors}
        handleTriggerValidation={triggerValidation}
        handleUpdateFieldValue={updateFieldValue}
        language={watchedValues.language}
      />
    </>
  );
}
